import { Sparkles } from "lucide-react";
import { useTypewriter } from "../lib/useTypewriter";

// Example queries surfaced in the global-search empty state. Each one maps to a
// real supported intent (a QuestionRegistry question or a Quick Action), so the
// hint advertises actual capability rather than raising false expectations.
export const SEARCH_EXAMPLES = [
  "who's absent today",
  "any overdue invoices",
  "log an incident for Bob",
  "birthdays this week",
  "ratio compliance status",
  "message a parent",
];

interface CapabilityHintProps {
  /** Optional context line above the examples, e.g. a no-results message. */
  note?: string;
  /** Override the default example set (mainly for tests). */
  examples?: string[];
}

// Fills the global-search dropdown's empty state with a guided "Try asking …"
// hint whose example phrases type themselves out one at a time. Turns the old
// silent empty/no-results state into a nudge toward what the Cmd-K bar can
// actually do. The animation is decorative — screen readers get a static,
// non-looping summary instead.
export function CapabilityHint({ note, examples = SEARCH_EXAMPLES }: CapabilityHintProps) {
  const { text, reducedMotion } = useTypewriter(examples);

  return (
    <div className="px-4 py-8 text-center">
      {note && <div className="mb-4 text-sm text-muted">{note}</div>}

      <div className="flex items-center justify-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted">
        <Sparkles size={12} className="text-accent" />
        Try asking
      </div>

      {/* Animated line is decorative; aria-hidden keeps it from spamming
          assistive tech as characters change. */}
      <div className="mt-2 min-h-[1.25rem] text-sm text-foreground" aria-hidden="true">
        <span>{text}</span>
        <span className={`search-caret ml-px text-accent ${reducedMotion ? "search-caret--static" : ""}`}>
          |
        </span>
      </div>

      {/* Static, screen-reader-only equivalent of the capability list. */}
      <span className="sr-only">You can ask things like: {examples.join(", ")}.</span>
    </div>
  );
}
